import { GET_ORIGIN, WWW_ORIGIN } from '../config';

export const deepTrimAndRemoveDuplicateSpaces = (obj) => {
  const cleanedObj = obj;
  Object.keys(obj).forEach((key) => {
    const value = obj[key];
    const type = typeof value;
    if (
      value != null &&
      (type === 'string' || type === 'object') &&
      Object.prototype.hasOwnProperty.call(obj, key)
    ) {
      if (type === 'object') {
        deepTrimAndRemoveDuplicateSpaces(obj[key]);
      } else {
        // regex replace removes duplicate spacing
        // 2 or more contiguous spaces are replaced by a single space
        cleanedObj[key] = obj[key].trim().replace(/ +/g, ' ');
      }
    }
  });
  return cleanedObj;
};

export const getCardImageData = (brand) => {
  if (brand === 'visa') {
    return {
      imageUrl: 'https://assets.xometry.com/images/Visa.svg',
      altText: brand,
    };
  }
  if (brand === 'mastercard') {
    return {
      imageUrl: 'https://assets.xometry.com/images/MasterCard.svg',
      altText: brand,
    };
  }
  if (brand === 'amex') {
    return {
      imageUrl: 'https://assets.xometry.com/images/Amex.svg',
      altText: brand,
    };
  }
  if (brand === 'discover') {
    return {
      imageUrl: 'https://assets.xometry.com/images/Discover.svg',
      altText: brand,
    };
  }
  return {
    imageUrl: '',
    altText: brand,
  };
};

/**
 * This helper function ensure that the returned value contain the correct value for redirection
 * with backwards compatibility with others projects.
 * @see https://github.com/xometry/payments-frontend/pull/117
 * @param providedRedirectPath Value from queryString.parse(window.location.search).redirect value
 * @returns Safe redirect param to window.location.assign(`${WWW_ORIGIN}/${parsedRedirectPath}`);
 */
export const parseRedirectQueryPath = (providedRedirectPath) => {
  let parsedRedirectPath = Array.isArray(providedRedirectPath)
    ? providedRedirectPath[0]
    : providedRedirectPath || '';

  // avoid duplicate WWW_ORIGIN on the redirect url (backwards compatibility)
  parsedRedirectPath = parsedRedirectPath.replace(WWW_ORIGIN, '');

  // avoid double slash
  if (parsedRedirectPath.startsWith('/')) {
    parsedRedirectPath = parsedRedirectPath.substring(1);
  }

  return parsedRedirectPath;
};

/**
 * This helper function copies the passed in object and them removes empty strings, null and undefined from it.
 * @param object
 * @example
 * returnNonEmptyCopiedObject({"key1": "value1", "key2": "", "key3": null, "key4": "value4"}) => {"key1": "value1", "key4": "value4"}
 * @returns {object} copied object without empty strings, null, or undefined.
 */
export const returnNonEmptyCopiedObject = (obj) => {
  const objectCopy = { ...obj };
  for (const propName in objectCopy) {
    if (
      objectCopy[propName] === null ||
      objectCopy[propName] === undefined ||
      objectCopy[propName] === ''
    ) {
      delete objectCopy[propName];
    }
  }
  return objectCopy;
};

export const centsToDollars = (amount) => {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  });
  return formatter.format(amount / 100.0);
};

/**
 * Formats a numeric string amount to USD currency format.
 * @param amount - The amount as a string (e.g., "1234.56")
 * @param currency - The currency code (defaults to 'USD')
 * @returns Formatted currency string (e.g., "$1,234.56")
 */
export const formatCurrency = (amount: string, currency = 'USD'): string => {
  const numericAmount = parseFloat(amount);
  if (isNaN(numericAmount)) {
    return amount;
  }
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  });
  return formatter.format(numericAmount);
};

/**
 * Formats an ISO date string to MM/DD/YYYY format.
 * @param dateString - ISO date string (e.g., "2024-01-15T00:00:00Z")
 * @returns Formatted date string (e.g., "01/15/2024")
 */
export const formatDateMMDDYYYY = (dateString: string): string => {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    return dateString;
  }
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
};

/**
 * This helper function takes a number in dollars and formats it use 'K' and 'M' amount symbols.
 * @param number
 * @example
 * numberMoneyFormatter(299792458) => 299.7M
 * numberMoneyFormatter(12388) => 12.3K
 * numberMoneyFormatter(1000) => 1K
 * numberMoneyFormatter(549) => 549
 * @returns truncated number with symbol
 */
export const numberMoneyFormatter: (num: number) => string = (num: number) => {
  // Return if number is 0;
  if (num === 0) return String(num);
  // Return 2 decimal places if number is fraction
  if (Math.abs(num) < 1) return num.toFixed(2);
  // Dict of amount and corresponding letter
  const moneySymbolDict = [
    { value: 1e6, symbol: 'M' },
    { value: 1e3, symbol: 'K' },
    { value: 1, symbol: '' },
  ];
  // Look through dict to see which symbol we need for the inputted num
  const moneySymbolObj = moneySymbolDict.find(
    (dict) => Math.abs(num) >= dict.value
  ) ?? { value: 1, symbol: '' };
  // Devide, round down and return the inputted num in truncated form (i.e. 1000 => 1, 1520952.66 => 1.5)
  // Note that this will still round down with negative numbers (-100.3 => -101)
  const truncatedNumber = Math.floor((num / moneySymbolObj.value) * 10) / 10;
  // Append symbol to truncatedNumber (i.e. 1 => 1K)
  return truncatedNumber.toString() + moneySymbolObj.symbol;
};

// If the destination is a relative path, build an absolute path to get.xometry.com
export const convertToQuoteSubdomain = (destination) => {
  if (
    !destination ||
    destination.startsWith('http') ||
    destination.startsWith('www')
  ) {
    return destination;
  }
  return `${GET_ORIGIN}${destination}`;
};

/**
 * Partner id for the shop owner only.
 *
 * Auth-context 5.x hydrated `user.company` only for the primary user, so
 * registration gated on `user.company.partner_id`. 7.0.0 hydrates
 * `user.partner` for every authorized member; `isPrimaryUser` restores that
 * gate so members still take the Join Our Network path.
 */
export const getPrimaryPartnerId = (
  user?: {
    isPrimaryUser?: boolean;
    partner?: { partner_id?: string | null } | null;
  } | null
): string | undefined => {
  if (!user?.isPrimaryUser) {
    return undefined;
  }
  return user.partner?.partner_id || undefined;
};

/**
 * v5-shaped user for shop-finances-onboarding-form, which still reads
 * user.company.mailing_address. Auth-context 7.0.0 hydrates user.partner
 * for every member and no longer sets company; only the shop owner used
 * to get company, so only isPrimaryUser gets the alias. partner is omitted
 * so the remote form never depends on the v7 field.
 */
export const toShopFinancesOnboardingUser = <
  T extends { isPrimaryUser?: boolean; partner?: unknown }
>(
  user?: T | null
) => {
  if (!user) {
    return user;
  }
  const { partner, ...userWithoutPartner } = user;
  return {
    ...userWithoutPartner,
    company: user.isPrimaryUser ? partner : undefined,
  };
};

export const hasMoreThanAscii = (str: string) => {
  if (str.length) {
    return str.split('').some((char) => {
      return char.charCodeAt(0) > 127;
    });
  } else {
    return false;
  }
};

export default {
  deepTrimAndRemoveDuplicateSpaces,
  parseRedirectQueryPath,
  getPrimaryPartnerId,
  toShopFinancesOnboardingUser,
  hasMoreThanAscii,
};
